import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Unit } from "types/landlord/address";
import addressApi from "api/landlord/address";
import { toast } from "react-toastify";
import { useMedia } from "shared/providers";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { HeaderBreadcrumbSection } from "./HeaderBreadcrumbSection";
import { HeaderUnitTitleSection } from "./HeaderUnitTitleSection";
import { HeaderActionsSection } from "./HeaderActionsSection";
import { HeaderMobileDetails } from "./HeaderMobileDetails";
import { HeaderDesktopDetails } from "./HeaderDesktopDetails";
import { DeleteUnitModal } from "shared/app/UnitsTableView/components/DeleteUnitModal";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";

interface LayoutHeaderProps {
  unit?: Unit;
}

export const LayoutHeader = ({ unit }: LayoutHeaderProps) => {
  const { isMobile } = useMedia();
  const [showDetails, setShowDetails] = useState(false);
  const [showInviteApplicantsModal, setShowInviteApplicantsModal] =
    useState(false);
  const dictionary = useUnitsPropertiesDictionary();
  const [showDeleteUnitModal, setShowDeleteUnitModal] = useState(false);
  const navigate = useNavigate();

  const deleteUnit = () => {
    return toast
      .promise(addressApi.deleteUnit(Number(unit?.id)), {
        pending: `Deleting ${dictionary.unit}...`,
        success: `${dictionary.Unit} deleted`,
        error: `Failed to delete ${dictionary.unit}`
      })
      .then(() => {
        setShowDeleteUnitModal(false);
        navigate("/units");
      });
  };

  return (
    <>
      <HeaderBreadcrumbSection
        unit={unit}
        onDeleteUnit={() => setShowDeleteUnitModal(true)}
      />

      <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-y-2 border-b border-alpha-40 px-6 md:py-4">
        <HeaderUnitTitleSection unit={unit} />
        <HeaderActionsSection
          unit={unit}
          onInviteApplicants={() => setShowInviteApplicantsModal(true)}
        />
      </div>

      {isMobile ? (
        <HeaderMobileDetails
          unit={unit}
          showDetails={showDetails}
          onToggleDetails={setShowDetails}
        />
      ) : (
        <HeaderDesktopDetails unit={unit} />
      )}

      {showInviteApplicantsModal && (
        <InviteAndCreateModal
          open={showInviteApplicantsModal}
          setOpen={() => setShowInviteApplicantsModal(false)}
          unitId={unit?.id}
          unitName={unit?.title}
          propertyId={Number(unit?.property.id || 0)}
          propertyName={unit?.property.name}
          onInviteRefreshKey={["APPLICANTS_TABLE"]}
          unitSelectSkipped={false}
        />
      )}

      <DeleteUnitModal
        opened={showDeleteUnitModal}
        onClose={() => setShowDeleteUnitModal(false)}
        onDelete={deleteUnit}
      />
    </>
  );
};
